// Everything on one day, grouped by job.
//
// Lifted out of CalendarPage in phase 7 stage 3 and rebuilt on the shared Modal,
// which brings the focus trap and the dialog stack with it — the old hand-rolled
// backdrop had neither, so Tab walked straight out into the page behind it.
//
// Grouped by job rather than listed flat because that is how a manager thinks
// about a day: "who is on Smith Street?", not "here are eleven bookings".
import { useMemo } from 'react';
import { Modal } from '../ui';
import { dateRangeLabel, formatClockRange } from '../../lib/dates';
import { type NwLookup } from '../../lib/nonWorkingDays';
import { jobColour } from '../../lib/jobColour';
import type { CalendarBooking, Resource, Unavailability } from '../../schedulingApi';

export function DayPanel({
  date,
  bookings,
  away,
  resources,
  onClose,
  onEdit,
  onAdd,
  onOpenJob,
  nwInfo,
}: {
  date: string;
  bookings: CalendarBooking[];
  away: Unavailability[];
  resources: Resource[];
  onClose: () => void;
  onEdit: (booking: CalendarBooking) => void;
  onAdd: () => void;
  /** A job heading opens the per-job panel — the tick-to-bulk-edit view. */
  onOpenJob: (jobId: string) => void;
  nwInfo: NwLookup;
}) {
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { key: string; jobId: string | null; number: string; title: string; items: CalendarBooking[] }
    >();
    for (const booking of bookings) {
      // Maintenance belongs on this panel — "what is happening today" includes
      // the machine that went to the mechanic. It has no job to group under,
      // so each maintenance booking is its own entry, headed by the supplier.
      const key = booking.jobId ?? `maintenance:${booking.id}`;
      const entry = map.get(key);
      if (entry) entry.items.push(booking);
      else
        map.set(key, {
          key,
          jobId: booking.jobId,
          number: booking.jobNumber ?? 'Maintenance',
          title: booking.jobTitle ?? (booking.supplierName ?? 'Off the road'),
          items: [booking],
        });
    }
    return [...map.values()];
  }, [bookings]);

  const busy = new Set(bookings.map((b) => b.resourceId));
  const awayIds = new Set(away.map((a) => a.resourceId));
  const free = resources.filter(
    (r) => r.status === 'available' && !busy.has(r.id) && !awayIds.has(r.id)
  );
  const resourceName = (id: string) => resources.find((r) => r.id === id)?.name ?? 'A resource';

  // "Monday, 31 August 2026" — ZT's day-panel title. The panel is the one
  // place a single day gets the floor, so it gets the day's full name.
  const longDate = new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const nw = nwInfo(date);

  return (
    <Modal
      title={longDate}
      onClose={onClose}
      size="xl"
      dismissOnBackdrop
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Close
          </button>
          <button type="button" onClick={onAdd} className="btn-primary btn-sm">
            Book something
          </button>
        </>
      }
    >
      {nw && (
        <p className="mb-3">
          <span className="inline-block rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-red-700">
            {nw.dated ? nw.name : 'Weekend'}
          </span>
        </p>
      )}

      {grouped.length === 0 && away.length === 0 && (
        <p className="py-4 text-center text-sm text-gray-500">Nothing booked on this day.</p>
      )}

      {grouped.map((group) => {
        const colour = jobColour(group.number, group.jobId, group.items.some((i) => i.inConflict));
        return (
          <section key={group.key} className="mb-4 last:mb-0">
            {group.jobId ? (
              <button
                type="button"
                onClick={() => onOpenJob(group.jobId!)}
                className={`mb-1.5 rounded border-l-4 pl-2 pr-1 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.stripe}`}
              >
                {group.number} · {group.title}
              </button>
            ) : (
              <h3
                className={`mb-1.5 border-l-4 pl-2 text-sm font-semibold text-gray-900 ${colour.stripe}`}
              >
                {group.number} · {group.title}
              </h3>
            )}
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="data-table">
                <tbody className="divide-y divide-gray-100">
                  {group.items.map((booking) => (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {booking.resourceName}
                        {booking.inConflict && (
                          <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[0.65rem] font-medium text-red-700 ring-1 ring-inset ring-red-600/25">
                            Needs re-planning
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {dateRangeLabel(booking.startDate, booking.endDate)}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {formatClockRange(booking.startTime, booking.endTime) ?? 'All day'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{booking.note}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" className="row-action" onClick={() => onEdit(booking)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {away.length > 0 && (
        <section className="mt-4">
          <h3 className="mb-1.5 text-sm font-semibold text-gray-900">Away</h3>
          <ul className="schedule-away rounded-md px-3 py-2 text-sm text-gray-700">
            {away.map((entry) => (
              <li key={entry.id}>
                <span className="font-medium">{resourceName(entry.resourceId)}</span> · {entry.reason}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-4">
        <h3 className="mb-1.5 text-sm font-semibold text-gray-900">Still free</h3>
        <p className="text-sm text-gray-500">
          {free.length === 0 ? 'Everyone is committed.' : free.map((r) => r.name).join(', ')}
        </p>
      </section>
    </Modal>
  );
}
